import React, { useEffect, useState } from 'react';
import { X, PhoneCall, Loader2, Clock, MessageSquare } from 'lucide-react';
import { leadCrm } from '../../services/dealerApi';
import { toast } from 'sonner';
import { formatUtcDatetime } from '../../utils/formatUtcDate';

const CALL_STATUSES = [
  { value: 'connected', label: 'Connected', tone: 'emerald' },
  { value: 'not_answering', label: 'Not Answering', tone: 'amber' },
  { value: 'busy', label: 'Busy', tone: 'amber' },
  { value: 'switched_off', label: 'Switched Off', tone: 'slate' },
  { value: 'invalid_number', label: 'Invalid Number', tone: 'red' },
];

// Call-outcomes where offering a WhatsApp fallback makes sense — the
// customer didn't pick up, so a WhatsApp nudge is often the next best
// step. INVALID_NUMBER is excluded (message would bounce anyway) and
// CONNECTED is excluded (already spoke to them).
const WHATSAPP_ELIGIBLE_STATUSES = new Set(['not_answering', 'busy', 'switched_off']);

const INTEREST_LEVELS = [
  { value: 'high', label: 'High', tone: 'emerald' },
  { value: 'medium', label: 'Medium', tone: 'amber' },
  { value: 'low', label: 'Low', tone: 'slate' },
];

const NEXT_ACTIONS = [
  { value: 'showroom_visit', label: 'Showroom Visit', needsDatetime: true },
  { value: 'test_drive', label: 'Test Drive', needsDatetime: true },
  { value: 'followup', label: 'Needs Follow-up', needsDatetime: true },
  { value: 'converted', label: 'Converted', needsDatetime: false, tone: 'emerald' },
  { value: 'not_interested', label: 'Not Interested', needsDatetime: false, tone: 'red' },
];

// Format a Date into the value string expected by <input type="datetime-local"> in LOCAL time.
const toLocalInput = (d) => {
  const off = d.getTimezoneOffset() * 60000;
  return new Date(d.getTime() - off).toISOString().slice(0, 16);
};

const DATETIME_PRESETS = [
  { label: '+1 hr', build: () => { const d = new Date(); d.setHours(d.getHours() + 1, 0, 0, 0); return toLocalInput(d); } },
  { label: 'Today 6 PM', build: () => { const d = new Date(); d.setHours(18, 0, 0, 0); return toLocalInput(d); } },
  { label: 'Tomorrow 10 AM', build: () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(10, 0, 0, 0); return toLocalInput(d); } },
  { label: 'Tomorrow 5 PM', build: () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(17, 0, 0, 0); return toLocalInput(d); } },
  { label: '+2 days 11 AM', build: () => { const d = new Date(); d.setDate(d.getDate() + 2); d.setHours(11, 0, 0, 0); return toLocalInput(d); } },
  { label: 'Weekend 12 PM', build: () => { const d = new Date(); const dow = d.getDay(); const add = ((6 - dow) + 7) % 7 || 7; d.setDate(d.getDate() + add); d.setHours(12, 0, 0, 0); return toLocalInput(d); } },
];

const toneCls = (tone, active) => {
  const map = {
    emerald: active ? 'bg-emerald-500 text-white border-emerald-400' : 'border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10',
    amber: active ? 'bg-amber-500 text-white border-amber-400' : 'border-amber-500/40 text-amber-300 hover:bg-amber-500/10',
    red: active ? 'bg-red-500 text-white border-red-400' : 'border-red-500/40 text-red-300 hover:bg-red-500/10',
    slate: active ? 'bg-slate-500 text-white border-slate-400' : 'border-slate-500/40 text-slate-300 hover:bg-slate-500/10',
    cyan: active ? 'bg-cyan-500 text-white border-cyan-400' : 'border-cyan-500/40 text-cyan-300 hover:bg-cyan-500/10',
  };
  return `px-3 py-2 rounded-xl text-sm font-semibold border-2 transition-colors ${map[tone] || map.cyan}`;
};

/**
 * Structured call-update form.
 *
 * onClose(updatedLead?) fires with the fresh lead payload when the update
 * succeeds so parents can update their in-memory list without a full refetch.
 */
export default function LeadUpdateModal({ lead, onClose }) {
  const [callStatus, setCallStatus] = useState(null);
  const [interest, setInterest] = useState(null);
  const [nextAction, setNextAction] = useState(null);
  const [scheduledAt, setScheduledAt] = useState('');
  const [notes, setNotes] = useState('');
  const [sendWhatsApp, setSendWhatsApp] = useState(false);
  const [saving, setSaving] = useState(false);
  const [timeline, setTimeline] = useState([]);

  useEffect(() => {
    if (!lead?.id) return;
    leadCrm.listActivities(lead.id).then((r) => setTimeline(r.activities || [])).catch(() => {});
  }, [lead?.id]);

  const canSubmit = () => {
    if (!callStatus) return false;
    if (callStatus !== 'connected') return true;
    if (!nextAction) return false;
    const naDef = NEXT_ACTIONS.find((a) => a.value === nextAction);
    if (naDef?.needsDatetime && !scheduledAt) return false;
    return true;
  };

  const submit = async () => {
    if (!canSubmit()) return;
    setSaving(true);
    try {
      const payload = { call_status: callStatus };
      if (callStatus === 'connected') {
        if (interest) payload.interest_level = interest;
        if (nextAction) {
          payload.next_action = nextAction;
          const naDef = NEXT_ACTIONS.find((a) => a.value === nextAction);
          if (naDef?.needsDatetime && scheduledAt) {
            // The datetime-local input value is local wall clock. Convert to ISO with tz offset.
            payload.next_action_at = new Date(scheduledAt).toISOString();
          }
        }
      }
      if (notes.trim()) payload.notes = notes.trim();
      // WhatsApp fallback only when the call didn't connect and the
      // telecaller explicitly ticked the box.
      if (sendWhatsApp && WHATSAPP_ELIGIBLE_STATUSES.has(callStatus)) {
        payload.send_whatsapp = true;
      }
      const res = await leadCrm.addActivity(lead.id, payload);
      if (res.whatsapp_queued) {
        toast.success('Lead updated · WhatsApp confirmation queued');
      } else {
        toast.success('Lead updated');
      }
      onClose?.(res.lead);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update lead');
    } finally {
      setSaving(false);
    }
  };

  if (!lead) return null;

  const naDef = NEXT_ACTIONS.find((a) => a.value === nextAction);
  const isConnected = callStatus === 'connected';

  return (
    <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-sm flex items-center justify-center p-3" onClick={() => onClose?.()} data-testid="lead-update-modal">
      <div
        className="bg-slate-900 border border-slate-700 rounded-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b border-slate-800 flex items-start justify-between gap-3 sticky top-0 bg-slate-900 z-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-cyan-400 font-bold">Call Update</div>
            <h3 className="text-xl font-extrabold text-white tracking-tight mt-1">{lead.customer_name}</h3>
            <div className="text-sm text-slate-400 mt-1">
              {lead.mobile_number} {lead.interested_car ? `· ${lead.interested_car}` : ''}
            </div>
          </div>
          <button onClick={() => onClose?.()} className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5" data-testid="lead-modal-close">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form body */}
        <div className="p-5 space-y-5">
          {/* 1. Call Status */}
          <section>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-bold block mb-2">1. Call Status *</label>
            <div className="flex flex-wrap gap-2">
              {CALL_STATUSES.map((s) => (
                <button
                  key={s.value}
                  onClick={() => {
                    setCallStatus(s.value);
                    // Reset the WhatsApp toggle whenever the outcome changes
                    // so it doesn't stay checked from a previous selection.
                    setSendWhatsApp(false);
                  }}
                  className={toneCls(s.tone, callStatus === s.value)}
                  data-testid={`call-status-${s.value}`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </section>

          {/* WhatsApp fallback — only for statuses where the customer
              didn't pick up. INVALID_NUMBER excluded (would bounce). */}
          {WHATSAPP_ELIGIBLE_STATUSES.has(callStatus) && (
            <section data-testid="whatsapp-fallback-section">
              <label className="flex items-start gap-3 p-3 rounded-xl bg-emerald-500/5 border border-emerald-500/30 cursor-pointer">
                <input
                  type="checkbox"
                  checked={sendWhatsApp}
                  onChange={(e) => setSendWhatsApp(e.target.checked)}
                  data-testid="lead-send-whatsapp"
                  className="mt-0.5 w-4 h-4 accent-emerald-500"
                />
                <div className="flex-1">
                  <div className="text-sm font-semibold text-white flex items-center gap-1.5">
                    <MessageSquare className="w-4 h-4 text-emerald-400" /> Send WhatsApp message now
                  </div>
                  <div className="text-xs text-slate-400 mt-0.5">
                    Recommended when the customer didn&apos;t pick up. Sends the standard confirmation template so they can respond when free.
                  </div>
                </div>
              </label>
            </section>
          )}

          {/* Conditional: only when Connected */}
          {isConnected && (
            <>
              <section>
                <label className="text-xs uppercase tracking-wider text-slate-400 font-bold block mb-2">2. Interest Level</label>
                <div className="flex flex-wrap gap-2">
                  {INTEREST_LEVELS.map((s) => (
                    <button
                      key={s.value}
                      onClick={() => setInterest(s.value)}
                      className={toneCls(s.tone, interest === s.value)}
                      data-testid={`interest-${s.value}`}
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </section>

              <section>
                <label className="text-xs uppercase tracking-wider text-slate-400 font-bold block mb-2">3. Next Action *</label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {NEXT_ACTIONS.map((a) => (
                    <button
                      key={a.value}
                      onClick={() => setNextAction(a.value)}
                      className={toneCls(a.tone || 'cyan', nextAction === a.value)}
                      data-testid={`next-action-${a.value}`}
                    >
                      {a.label}
                    </button>
                  ))}
                </div>

                {naDef?.needsDatetime && (
                  <div className="mt-3 space-y-2">
                    <label className="text-xs text-slate-400 flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" /> Scheduled date &amp; time *
                    </label>
                    <input
                      type="datetime-local"
                      value={scheduledAt}
                      min={toLocalInput(new Date())}
                      onChange={(e) => setScheduledAt(e.target.value)}
                      data-testid="next-action-datetime"
                      style={{ colorScheme: 'dark' }}
                      className="w-full px-3 py-2.5 rounded-lg bg-slate-950/70 border border-slate-700 text-white text-sm focus:border-cyan-500 outline-none"
                    />
                    <div className="flex flex-wrap gap-1.5">
                      {DATETIME_PRESETS.map((p) => (
                        <button
                          key={p.label}
                          type="button"
                          onClick={() => setScheduledAt(p.build())}
                          className="px-2.5 py-1 rounded-md bg-slate-800 hover:bg-slate-700 border border-slate-700 text-slate-300 text-xs font-semibold"
                          data-testid={`preset-${p.label.toLowerCase().replace(/\s+/g, '-')}`}
                        >
                          {p.label}
                        </button>
                      ))}
                    </div>
                    {scheduledAt && (
                      <div className="text-xs text-cyan-300" data-testid="scheduled-preview">
                        → {new Date(scheduledAt).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}
                      </div>
                    )}
                  </div>
                )}
              </section>
            </>
          )}

          {/* 4. Notes — always shown */}
          <section>
            <label className="text-xs uppercase tracking-wider text-slate-400 font-bold block mb-2">
              {isConnected ? '4. Lead Notes' : '2. Notes'} <span className="text-slate-600 font-normal">(optional)</span>
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
              placeholder="e.g. Customer is interested in Hyundai Creta. Budget ₹15L. Wants finance options. Requested showroom visit on Friday at 5 PM."
              data-testid="lead-notes"
              className="w-full px-3 py-2 rounded-lg bg-slate-950/70 border border-slate-700 text-white text-sm placeholder-slate-500 focus:border-cyan-500 outline-none"
            />
          </section>

          {/* Timeline preview */}
          {timeline.length > 0 && (
            <section>
              <label className="text-xs uppercase tracking-wider text-slate-400 font-bold block mb-2">Recent activity ({timeline.length})</label>
              <div className="space-y-2 max-h-56 overflow-y-auto pr-1" data-testid="lead-timeline">
                {timeline.slice(0, 8).map((a) => (
                  <div key={a.id} className="text-xs bg-slate-950/40 border border-slate-800 rounded-lg p-2.5">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-slate-300 font-semibold">
                        {a.call_status ? a.call_status.replace(/_/g, ' ') : (a.outcome || 'update')}
                        {a.interest_level && <span className="ml-2 text-cyan-400">· {a.interest_level}</span>}
                        {a.next_action && <span className="ml-2 text-amber-300">· {a.next_action.replace(/_/g, ' ')}</span>}
                      </div>
                      <span className="text-slate-500">{formatUtcDatetime(a.ts)}</span>
                    </div>
                    {a.next_action_at && (
                      <div className="text-slate-500 mt-0.5">→ scheduled {formatUtcDatetime(a.next_action_at)}</div>
                    )}
                    {a.notes && <div className="text-slate-400 mt-1 whitespace-pre-line">{a.notes}</div>}
                    <div className="text-slate-600 mt-1">— {a.telecaller_name}</div>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-800 flex items-center justify-end gap-2 sticky bottom-0 bg-slate-900">
          <button onClick={() => onClose?.()} className="px-4 py-2 rounded-lg text-slate-400 hover:text-white text-sm" data-testid="lead-modal-cancel">
            Cancel
          </button>
          <button
            onClick={submit}
            disabled={!canSubmit() || saving}
            data-testid="lead-modal-save"
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-cyan-500 hover:bg-cyan-400 text-slate-950 text-sm font-bold disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <PhoneCall className="w-4 h-4" />} Save update
          </button>
        </div>
      </div>
    </div>
  );
}
